import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HeartPulse, Phone, LifeBuoy, ShieldAlert } from "lucide-react";

export default function Safety() {
  const t = useT();
  const qc = useQueryClient();

  const contact = useQuery({
    queryKey: ["emergency-contact"],
    queryFn: async () => (await api.get("/emergency-contact")).data,
    retry: false,
  });
  const helplines = useQuery({
    queryKey: ["helplines"],
    queryFn: async () => (await api.get("/safety/helplines")).data,
    retry: false,
  });
  const recovery = useQuery({
    queryKey: ["recovery-state"],
    queryFn: async () => (await api.get("/recovery/state")).data,
    retry: false,
  });

  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactRelation, setContactRelation] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  React.useEffect(() => {
    if (contact.data) {
      setContactName(contact.data.name || contact.data.contact_name || "");
      setContactPhone(contact.data.phone || contact.data.contact_phone || "");
      setContactRelation(contact.data.relation || contact.data.relationship || "");
    }
  }, [contact.data]);

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.post("/emergency-contact", {
        name: contactName,
        phone: contactPhone,
        relation: contactRelation,
      });
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["emergency-contact"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingContact(false); }
  };

  const alertNow = async () => {
    try {
      await api.post("/safety/alert-now");
      toast.success(t("client.alertNow"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const acknowledge = async () => {
    try {
      await api.post("/safety/acknowledge");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["recovery-state"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const slipping = async () => {
    try {
      await api.post("/safety/slipping");
      toast.success(t("client.slippingHelp"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const linesRaw = helplines.data?.helplines || helplines.data?.lines || (Array.isArray(helplines.data) ? helplines.data : []);
  const lines = Array.isArray(linesRaw) ? linesRaw : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.safety")} title={t("client.safetyTitle")} subtitle={t("client.safetySub")} />

      <div className="rounded-3xl bg-white shadow-card p-6 md:p-8 border-l-4 border-rust-500" data-testid="safety-crisis-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-rust-500/10 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-rust-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl text-ink-900">{t("client.ifCrisis")}</h2>
            <p className="text-ink-500 text-sm">{t("client.ifCrisisSub")}</p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button data-testid="safety-alert" onClick={alertNow} className="rounded-full bg-rust-500 hover:bg-rust-600 text-white h-11 px-5">
            <Phone className="h-4 w-4 mr-1.5" /> {t("client.alertNow")}
          </Button>
          <Button data-testid="safety-slipping" onClick={slipping} variant="outline" className="rounded-full h-11 px-5 border-border">
            {t("client.slippingHelp")}
          </Button>
          <Button data-testid="safety-acknowledge" onClick={acknowledge} variant="outline" className="rounded-full h-11 px-5 border-border">
            <HeartPulse className="h-4 w-4 mr-1.5" /> {t("client.acknowledgeAlert")}
          </Button>
        </div>
        {recovery.data?.state && (
          <div className="mt-4 text-xs text-ink-500">state: <span className="text-ink-900 font-medium">{recovery.data.state}</span></div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="safety-contact">
          <h3 className="font-heading text-lg text-ink-900">{t("client.emergencyContact")}</h3>
          {contact.isLoading && <LoadingCard height="h-24" />}
          <div className="mt-4 space-y-3">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="safety-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="safety-contact-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
            </div>
            <div>
              <Label>Relation</Label>
              <Input data-testid="safety-contact-relation" value={contactRelation} onChange={(e) => setContactRelation(e.target.value)} className="rounded-xl bg-cream-50 h-11 mt-1" />
            </div>
            <Button onClick={saveContact} data-testid="safety-save-contact" disabled={savingContact} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
              {savingContact ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="safety-helplines">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-sage-600" strokeWidth={1.75} />
            <h3 className="font-heading text-lg text-ink-900">{t("client.helplines")}</h3>
          </div>
          {helplines.isLoading && <LoadingCard height="h-24" />}
          <div className="mt-4 space-y-2">
            {lines.length === 0 && !helplines.isLoading && (
              <div className="text-sm text-ink-500">{t("common.empty")}</div>
            )}
            {lines.map((h, i) => (
              <a
                key={h.id || i}
                href={h.tel ? `tel:${h.tel}` : (h.url || "#")}
                target={h.url ? "_blank" : undefined}
                rel="noreferrer"
                className="block rounded-2xl border border-border bg-cream-50 hover:bg-white transition p-3"
                data-testid={`helpline-${i}`}
              >
                <div className="text-ink-900 font-medium">{h.name || h.title}</div>
                {(h.tel || h.phone) && <div className="text-xs text-ink-500">{h.tel || h.phone}</div>}
                {h.description && <div className="text-xs text-ink-500 mt-1">{h.description}</div>}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
